/**
 * MultiSelect — a multi-value dropdown: the searchable-popover sibling of the
 * native `Select`. The trigger summarises the selection ("Label" or "N
 * selected"); the panel is a checkbox list with an optional search box, a Clear
 * action, and an "X of Y" count in the footer.
 *
 * Presentational + props-driven: it owns only its open/query state, never data.
 */
import { For, Show, createMemo, createSignal, type JSX } from "solid-js";
import ChevronDown from "lucide-solid/icons/chevron-down";
import Check from "lucide-solid/icons/check";
import Search from "lucide-solid/icons/search";
import { Popover } from "./popover";
import { Button } from "./button";
import { cn } from "./utils/cn";
import { focusRing } from "./utils/focus-ring";
import type { SelectOption } from "./toolbar";

const triggerBase = cn(
  "inline-flex h-8 w-full min-w-0 items-center justify-between gap-100 rounded-small",
  "border border-[color:var(--c9-border-input)] bg-[var(--c9-bg-input)] px-100 text-body text-[color:var(--c9-text)]",
  "hover:border-[color:var(--c9-border-bold)]",
);

export function MultiSelect<T extends string = string>(props: {
  value: T[];
  onChange: (value: T[]) => void;
  options: SelectOption<T>[];
  placeholder?: string;
  searchable?: boolean;
  width?: number;
  class?: string;
  label?: string;
}): JSX.Element {
  const [query, setQuery] = createSignal("");
  const filtered = createMemo(() => {
    if (props.searchable === false) return props.options;
    const q = query().trim().toLowerCase();
    return q ? props.options.filter((o) => o.label.toLowerCase().includes(q)) : props.options;
  });
  const toggle = (v: T) =>
    props.onChange(props.value.includes(v) ? props.value.filter((x) => x !== v) : [...props.value, v]);

  const summary = () => {
    if (props.value.length === 0) return props.placeholder ?? "Select";
    if (props.value.length === 1)
      return props.options.find((o) => o.value === props.value[0])?.label ?? props.value[0];
    return `${props.value.length} selected`;
  };

  return (
    <Popover
      width={props.width ?? 240}
      trigger={({ toggle: t, open, setRef }) => (
        <button
          ref={setRef}
          type="button"
          onClick={t}
          aria-haspopup="listbox"
          aria-expanded={open()}
          aria-label={props.label}
          class={cn(triggerBase, focusRing, props.class)}
        >
          <span class={cn("min-w-0 truncate", props.value.length === 0 && "text-[color:var(--c9-text-subtlest)]")}>
            {summary()}
          </span>
          <ChevronDown size={14} class="shrink-0 text-[color:var(--c9-icon-subtle)]" />
        </button>
      )}
    >
      {() => (
        <>
          <Show when={props.searchable !== false}>
            <div class="relative shrink-0 border-b border-[color:var(--c9-border)] p-100">
              <Search
                size={14}
                class="pointer-events-none absolute left-[18px] top-1/2 -translate-y-1/2 text-[color:var(--c9-icon-subtle)]"
              />
              <input
                type="search"
                value={query()}
                onInput={(e) => setQuery(e.currentTarget.value)}
                placeholder="Search"
                class={cn(
                  "h-8 w-full rounded-small border border-[color:var(--c9-border-input)] bg-[var(--c9-bg-input)]",
                  "pl-[26px] pr-100 text-body text-[color:var(--c9-text)] placeholder:text-[color:var(--c9-text-subtlest)]",
                  focusRing,
                )}
              />
            </div>
          </Show>

          <div class="min-h-0 flex-1 overflow-y-auto p-050">
            <For
              each={filtered()}
              fallback={<div class="px-100 py-100 text-body text-[color:var(--c9-text-subtlest)]">No matches</div>}
            >
              {(o) => {
                const checked = () => props.value.includes(o.value);
                return (
                  <button
                    type="button"
                    onClick={() => toggle(o.value)}
                    role="option"
                    aria-selected={checked()}
                    class={cn(
                      "flex w-full items-center gap-100 rounded-small px-100 py-075 text-left text-body text-[color:var(--c9-text)]",
                      "hover:bg-[var(--c9-bg-neutral-subtle-hovered)]",
                      focusRing,
                    )}
                  >
                    <span
                      class={cn(
                        "flex size-4 shrink-0 items-center justify-center rounded-xsmall border",
                        checked()
                          ? "border-[color:var(--c9-border-selected)] bg-[var(--c9-bg-selected-bold)] text-[color:var(--c9-text-inverse)]"
                          : "border-[color:var(--c9-border-input)]",
                      )}
                    >
                      <Show when={checked()}>
                        <Check size={12} stroke-width={3} />
                      </Show>
                    </span>
                    <span class="min-w-0 truncate">{o.label}</span>
                  </button>
                );
              }}
            </For>
          </div>

          <div class="flex shrink-0 items-center justify-between gap-200 border-t border-[color:var(--c9-border)] px-100 py-075">
            <Show
              when={props.value.length > 0}
              fallback={<span aria-hidden="true" />}
            >
              <Button appearance="subtle" size="compact" onClick={() => props.onChange([])}>
                Clear
              </Button>
            </Show>
            <span class="text-body-s font-[630] tabular-nums text-[color:var(--c9-text-subtlest)]">
              {props.value.length} of {props.options.length}
            </span>
          </div>
        </>
      )}
    </Popover>
  );
}
